import axios from 'axios';
import moment from 'moment';

const API_BASE = process.env.VUE_APP_API_BASE || '';

const SUPPORTED_TFS = ['1m', '3m', '5m', '10m', '15m', '30m', '1H', '2H', '4H', '1D', '1W', '1M'];
const HIGHER_TIMEFRAME_TFS = new Set(['1D', '1W', '1M']);

function encodeInstrumentKey(value) {
    const text = String(value || '').trim();
    if (typeof btoa === 'function') {
        return btoa(unescape(encodeURIComponent(text)));
    }
    return text;
}

export default {
    symbols: [],

    isEnabled() {
        try {
            return !!(window.__tc_store && window.__tc_store.getters.indianMarketDataEnabled);
        } catch {
            return false;
        }
    },

    async _getClerkToken() {
        for (let attempt = 0; attempt < 40; attempt++) {
            try {
                if (window.__vue_clerk && window.__vue_clerk.getToken) {
                    return await window.__vue_clerk.getToken();
                }
                if (window.Clerk && window.Clerk.session) {
                    return await window.Clerk.session.getToken();
                }
                if (window.Clerk && window.Clerk.getToken) {
                    return await window.Clerk.getToken();
                }
            } catch (e) {
                if (attempt === 39) {
                    console.warn('[IndianMarket] Could not get Clerk token:', e.message);
                }
            }

            await new Promise(resolve => setTimeout(resolve, 100));
        }
        return null;
    },

    async searchSymbols(query, limit = 30) {
        if (!query || query.length < 1) return [];

        try {
            const token = await this._getClerkToken();
            if (!token) return [];

            const response = await axios.get(API_BASE + '/api/indian-market/search', {
                params: {
                    query: query.toUpperCase(),
                    limit
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                this.symbols = response.data.symbols || [];
                return this.symbols;
            }
        } catch (error) {
            console.error('[IndianMarket] search error:', error);
        }
        return [];
    },

    async loadOptionChain(underlying, expiry = '') {
        if (!underlying || typeof underlying !== 'string') {
            throw new Error('Underlying symbol is required');
        }

        const token = await this._getClerkToken();
        if (!token) throw new Error('Not authenticated');

        const params = { underlying };
        if (expiry) params.expiry = expiry;

        const response = await axios.get(API_BASE + '/api/indian-market/option-chain', {
            params,
            headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.data || !response.data.success) {
            throw new Error(response.data?.error || 'Failed to load option chain');
        }

        return response.data;
    },

    async loadHistoricalData(symbol, timeframe = '15m', endTime) {
        if (!symbol || typeof symbol !== 'string') {
            throw new Error('Instrument key is required');
        }

        if (!SUPPORTED_TFS.includes(timeframe)) {
            throw new Error(`Invalid timeframe: ${timeframe}`);
        }

        const token = await this._getClerkToken();
        if (!token) throw new Error('Not authenticated');

        const response = await axios.get(API_BASE + '/api/indian-market/history', {
            params: {
                symbol: encodeInstrumentKey(symbol),
                timeframe,
                ...(Number.isFinite(endTime) ? { from: endTime } : {}),
                limit: HIGHER_TIMEFRAME_TFS.has(timeframe) ? 2500 : 500
            },
            headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.data || !response.data.success) {
            throw new Error(response.data?.error || 'Failed to load Indian market data');
        }

        const candles = response.data.data?.candles || [];
        return candles.map(c => {
            const ts = typeof c[0] === 'number' ? c[0] : moment(c[0]).valueOf();
            return [ts, c[1], c[2], c[3], c[4], c[5] || 0];
        }).sort((a, b) => a[0] - b[0]);
    }
};
